import React from 'react';
import { BaseTableProps } from '../types';
import IndeterminateCheckbox from '../IndeterminateCheckbox';
import { getCoreRowModel, PaginationState, Table, useReactTable } from '@tanstack/react-table';

export const useTableConfig = <T,>({
  data,
  columns: originColumns,
  sortingState,
  filteringState,
  rowSelectionState,
  paginationState,
  columnPinning,
  rowCount,
}: Omit<BaseTableProps<T>, 'emptyText'>): { table: Table<T>; pagination: PaginationState } => {
  const [sorting, setSorting] = sortingState ?? [];
  const [filtering, setFiltering] = filteringState ?? [];
  const [rowSelection, setRowSelection] = rowSelectionState ?? [];
  const [pagination, setPagination] = paginationState;

  const table = useReactTable({
    data,
    columns: rowSelectionState
      ? [
          {
            id: 'selection',
            header: ({ table }): JSX.Element => (
              <IndeterminateCheckbox
                checked={table.getIsAllRowsSelected()}
                indeterminate={table.getIsSomeRowsSelected()}
                onChange={table.getToggleAllRowsSelectedHandler()}
              />
            ),
            cell: ({ row }): JSX.Element => (
              <IndeterminateCheckbox
                checked={row.getIsSelected()}
                disabled={!row.getCanSelect()}
                indeterminate={row.getIsSomeSelected()}
                onChange={row.getToggleSelectedHandler()}
              />
            ),
            size: 50,
            enableColumnFilter: false,
          },
          ...originColumns,
        ]
      : originColumns,
    state: {
      sorting,
      columnFilters: filtering,
      rowSelection,
      pagination,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setFiltering,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    onPaginationChange: setPagination,
    rowCount,
    manualPagination: true,
    initialState: {
      columnPinning: {
        left: ['selection', ...columnPinning],
      },
    },
  });

  return { table, pagination };
};
